import React, { useEffect, useMemo, useRef, useState } from "react";
import CurrencyInput from "../CurrencyInput";
import {
  createPurchaseUnitDraft,
  getReceivingUnit,
  validatePurchaseUnitDraft,
} from "../../utils/purchaseUnitDraft";
import { findSharedUnitBarcodeConflicts } from "../../utils/inventoryUnits";
import PurchaseUnitDraftEditor from "./PurchaseUnitDraftEditor";

const focusableSelector =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function NewPurchaseItemDialog({
  barcode,
  categories = [],
  inventory = [],
  onConfirm,
  onCancel,
}) {
  const normalizedBarcode = String(barcode || "").trim();
  const [form, setForm] = useState({
    itemName: "",
    category: "",
    unitSellPrice: "",
  });
  const [units, setUnits] = useState(() => [createPurchaseUnitDraft({
    key: "base",
    unitName: "Unit",
    unitCode: "UNIT",
    conversionFactor: "1",
    isBaseUnit: true,
    barcode: normalizedBarcode,
  })]);
  const [receivingUnitKey, setReceivingUnitKey] = useState("base");
  const [errors, setErrors] = useState({});
  const dialogRef = useRef(null);
  const nameRef = useRef(null);

  useEffect(() => nameRef.current?.focus(), []);

  const categoryOptions = useMemo(
    () => categories.map((category) =>
      typeof category === "string" ? category : category.name,
    ).filter(Boolean),
    [categories],
  );
  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.itemName.trim()) next.itemName = "Item name is required.";
    if (!Number.isFinite(Number(form.unitSellPrice)) || Number(form.unitSellPrice) <= 0) {
      next.unitSellPrice = "Selling price is required and must be greater than zero.";
    }
    const unitErrors = validatePurchaseUnitDraft({
      units,
      receivingUnitKey,
      scannedBarcode: normalizedBarcode,
    });
    if (Object.keys(unitErrors).length) next.units = unitErrors;
    setErrors(next);
    return next;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) return;
    const conflicts = findSharedUnitBarcodeConflicts(
      units.map((unit) => ({
        unit_name: unit.unitName,
        conversion_factor: unit.conversionFactor,
        barcode: unit.barcode,
        is_base_unit: unit.isBaseUnit,
        is_active: true,
      })),
      inventory,
      null,
    );
    if (conflicts.length && !window.confirm(
      `Share packaging barcode? ${conflicts.map((conflict) => conflict.barcode).join(", ")} ` +
      "already belongs to another packaging unit.",
    )) return;
    const sharedIndexes = new Set(conflicts.flatMap((conflict) => conflict.unitIndexes));
    const approvedUnits = units.map((unit, index) => ({
      ...unit,
      allowSharedBarcode: sharedIndexes.has(index),
    }));
    const receivingUnit = getReceivingUnit(approvedUnits, receivingUnitKey);
    const conversionFactor = Number(receivingUnit.conversionFactor);
    const unitCode = String(receivingUnit.unitCode || receivingUnit.unitName)
      .trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").slice(0, 24);
    onConfirm({
      key: `new:${normalizedBarcode.toLowerCase()}`,
      itemId: null,
      productUnitId: null,
      barcode: normalizedBarcode,
      itemName: form.itemName.trim(),
      unitName: receivingUnit.unitName.trim(),
      unitCode,
      category: form.category || null,
      conversionFactor,
      quantity: "1",
      unitBuyPrice: "",
      unitSellPrice: form.unitSellPrice,
      currentUnitSellPrice: null,
      isNewItem: true,
      receivingUnitKey,
      newItemUnits: approvedUnits.map((unit) => ({
        ...unit,
        unitName: unit.unitName.trim(),
        unitCode: String(unit.unitCode || unit.unitName)
          .trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").slice(0, 24),
        barcode: String(unit.barcode || "").trim(),
      })),
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const controls = Array.from(
      dialogRef.current?.querySelectorAll(focusableSelector) || [],
    );
    if (!controls.length) return;
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="modal-overlay purchase-item-dialog-overlay">
      <div
        ref={dialogRef}
        className="modal purchase-item-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-purchase-item-title"
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <div>
            <p className="eyebrow">NEW PRODUCT</p>
            <h2 id="new-purchase-item-title">Add this scanned item</h2>
            <p>မရှိသေးသော ကုန်ပစ္စည်းကို အတိုချုံး ထည့်ပါ။</p>
          </div>
          <button type="button" className="close-btn" onClick={onCancel} aria-label="Cancel new purchase item">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="new-purchase-barcode">Scanned barcode</label>
            <input
              id="new-purchase-barcode"
              name="barcode"
              value={normalizedBarcode}
              readOnly
            />
            <small>စကင်ဖတ်ထားသော ဘားကုဒ်ကို မပြောင်းပါ။</small>
          </div>

          <div className="form-group">
            <label htmlFor="new-purchase-item-name">Item name *</label>
            <input
              ref={nameRef}
              id="new-purchase-item-name"
              name="itemName"
              value={form.itemName}
              onChange={update}
              aria-invalid={Boolean(errors.itemName)}
              aria-describedby="new-purchase-item-name-help new-purchase-item-name-error"
            />
            <small id="new-purchase-item-name-help">ကုန်ပစ္စည်းအမည်</small>
            {errors.itemName && <span id="new-purchase-item-name-error" className="form-error" role="alert">{errors.itemName}</span>}
          </div>

          <div className="purchase-item-dialog-grid purchase-item-parent-grid">
            <div className="form-group">
              <label htmlFor="new-purchase-category">Category (optional)</label>
              <select id="new-purchase-category" name="category" value={form.category} onChange={update}>
                <option value="">No category</option>
                {categoryOptions.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
              <small>အမျိုးအစား (မရွေးလည်းရသည်)</small>
            </div>
          </div>

          <PurchaseUnitDraftEditor
            units={units}
            receivingUnitKey={receivingUnitKey}
            scannedBarcode={normalizedBarcode}
            errors={errors.units || {}}
            onChange={(nextUnits) => {
              setUnits(nextUnits);
              setErrors((current) => ({ ...current, units: undefined }));
            }}
            onReceivingUnitChange={(key) => {
              setReceivingUnitKey(key);
              setErrors((current) => ({ ...current, units: undefined }));
            }}
          />

          <div className="form-group">
            <label htmlFor="new-purchase-sell-price">Selling price for the scanned unit *</label>
            <CurrencyInput
              id="new-purchase-sell-price"
              name="unitSellPrice"
              value={form.unitSellPrice}
              onChange={(value) => {
                setForm((current) => ({ ...current, unitSellPrice: value }));
                setErrors((current) => ({ ...current, unitSellPrice: undefined }));
              }}
              min={0}
              step={100}
              label="selling price"
              aria-invalid={Boolean(errors.unitSellPrice)}
            />
            <small>ဤယူနစ်အတွက် ရောင်းဈေး ထည့်ပါ။</small>
            {errors.unitSellPrice && <span className="form-error" role="alert">{errors.unitSellPrice}</span>}
          </div>

          <p className="purchase-unit-price-note">
            Other unit selling prices are calculated proportionally from this price.
          </p>

          <p className="purchase-item-dialog-note">
            Stock and base purchase price are calculated after you enter the received quantity and buying price.
            <span> လက်ခံသည့်အရေအတွက်နှင့် ဝယ်ဈေးမှ စတော့နှင့် အခြေခံဝယ်ဈေးကို အလိုအလျောက်တွက်မည်။</span>
          </p>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add to purchase</button>
          </div>
        </form>
      </div>
    </div>
  );
}
